import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { X } from 'lucide-react';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { cn } from '../../lib/utils';
import defaultTheme from 'tailwindcss/defaultTheme';
import type {
  DialogOverlayProps,
  DialogContentProps,
  DialogActionsProps,
  DialogTitleProps,
  DialogDescriptionProps,
} from './types';
import useI18n from '../../hooks/useI18n';
import { DialogContentContainerContext } from './useDialogContentContainer';
import { useFullscreenTableContainer } from '../Table/hooks/useFullscreenTableContainer';

/**
 * Whether Escape came from a control whose popup is open, such as a combobox or
 * the search input inside its popup.
 *
 * Radix hears Escape first, on the document, and knows only its own layers. A
 * Base UI popup is not one of them, so the dialog would close under it. Matching
 * `aria-haspopup` as well keeps an expanded accordion trigger from counting.
 */
function isFromOpenPopup(target: EventTarget | null) {
  return (
    target instanceof Element &&
    target.closest('[aria-haspopup][aria-expanded="true"]') !== null
  );
}

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...rest }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'eye:fixed eye:inset-[0] eye:z-1300 eye:grid eye:bg-black/80 eye:px-8 eye:py-12 eye:pt-xl' +
        ' eye:transition-opacity eye:duration-200 eye:ease-out eye:data-[state=closed]:pointer-events-none eye:data-[state=closed]:opacity-0 eye:data-[state=open]:opacity-100 eye:sm:py-64',
      'eye:place-items-center',
      'eye:overflow-y-auto',
      className,
    )}
    {...rest}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Radix links Content to its Description by default but warns when that
// Description is missing, so Content opts out until one has mounted.
const DescriptionPresenceContext = React.createContext<
  React.Dispatch<React.SetStateAction<number>> | undefined
>(undefined);

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      maxWidth = 'sm',
      persistent = false,
      style,
      container,
      overlayClassName,
      ['aria-describedby']: ariaDescribedBy,
      ...rest
    },
    ref,
  ) => {
    const { t } = useI18n();
    // Radix restores focus to its own Trigger and to nothing else. Most of our
    // dialogs are controlled and have no Trigger, so Radix suppresses the
    // built-in restore and then focuses null — closing drops focus to <body>
    // (WCAG 2.4.3). Remember the opener as a fallback for exactly that case.
    const openerRef = React.useRef<HTMLElement | null>(null);
    const fullscreenContainer = useFullscreenTableContainer();

    const [contentNode, setContentNode] = React.useState<HTMLElement | null>(
      null,
    );
    const contentRef = useComposedRefs(ref, setContentNode);

    const portalContainer = container ?? fullscreenContainer;
    const [descriptionCount, setDescriptionCount] = React.useState(0);

    return (
      <DialogPortal container={portalContainer}>
        <DialogOverlay className={overlayClassName}>
          <DialogPrimitive.Content
            ref={contentRef}
            data-slot='dialog-content'
            className={cn(
              'eye:bg-white',
              'eye:p-s eye:md:p-l',
              'eye:rounded-lg eye:border eye:transition-all eye:duration-200 eye:ease-out eye:data-[state=closed]:scale-95 eye:data-[state=closed]:opacity-0 eye:data-[state=open]:scale-100 eye:data-[state=open]:opacity-100',
              'group/dialog-content',
              // Receives focus on open but is not operable itself.
              'eye:outline-none',
              className,
            )}
            style={{
              maxWidth: `calc(${defaultTheme.screens[maxWidth]} - 32px)`,
              ...style,
            }}
            data-has-close-button={!persistent}
            {...(persistent && {
              onPointerDownOutside: (e) => e.preventDefault(),
            })}
            {...((ariaDescribedBy !== undefined || descriptionCount === 0) && {
              'aria-describedby': ariaDescribedBy,
            })}
            {...rest}
            onEscapeKeyDown={(event) => {
              rest.onEscapeKeyDown?.(event);
              if (persistent || isFromOpenPopup(event.target)) {
                event.preventDefault();
              }
            }}
            onOpenAutoFocus={(event) => {
              rest.onOpenAutoFocus?.(event);
              // Fires before Radix moves focus, so this is still the opener.
              const opener = document.activeElement;
              openerRef.current =
                opener instanceof HTMLElement && opener !== document.body
                  ? opener
                  : null;
              if (event.defaultPrevented) return;
              // Radix would focus the first tabbable, which is now the close
              // button, so screen readers would open on "Close". Focusing the
              // container announces the dialog's name instead. Radix dispatches
              // this event on the container, and a child with autoFocus skips it.
              event.preventDefault();
              (event.target as HTMLElement).focus();
            }}
            onCloseAutoFocus={(event) => {
              rest.onCloseAutoFocus?.(event);
              if (event.defaultPrevented) return;
              const opener = openerRef.current;
              openerRef.current = null;
              if (!opener?.isConnected) return;
              // Radix runs after this and restores its Trigger, so only step in
              // once it has focused nothing at all.
              setTimeout(() => {
                const focused = document.activeElement;
                if (!focused || focused === document.body) opener.focus();
              }, 0);
            }}
          >
            {/* First in the DOM so the tab order matches its visual position (WCAG 2.4.3). */}
            {!persistent && (
              <DialogPrimitive.Close
                asChild
                className='eye:absolute eye:top-xs eye:right-xs eye:z-10 eye:disabled:pointer-events-none'
              >
                <Button variant='ghost' size='icon'>
                  <X className='eye:h-24 eye:w-24' />
                  <span className='eye:sr-only'>{t('close')}</span>
                </Button>
              </DialogPrimitive.Close>
            )}
            <DialogContentContainerContext.Provider value={contentNode}>
              <DescriptionPresenceContext.Provider value={setDescriptionCount}>
                {children}
              </DescriptionPresenceContext.Provider>
            </DialogContentContainerContext.Provider>
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogActions = ({
  className,
  children,
  ...rest
}: DialogActionsProps) => (
  <div
    className={cn(
      'eye:mt-xs eye:flex eye:flex-row eye:items-center eye:justify-end eye:gap-xs eye:max-sm:justify-center',
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);
DialogActions.displayName = 'DialogActions';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, children, ...rest }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'eye:text-lg eye:tracking-tight eye:mb-xs eye:pr-2xs eye:text-center eye:leading-none eye:font-semibold eye:sm:text-left',
      className,
    )}
    {...rest}
  >
    <Typography variant='headline-l'>{children}</Typography>
  </DialogPrimitive.Title>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, children, asChild, ...rest }, ref) => {
  const setDescriptionCount = React.useContext(DescriptionPresenceContext);

  React.useLayoutEffect(() => {
    setDescriptionCount?.((count) => count + 1);
    return () => setDescriptionCount?.((count) => count - 1);
  }, [setDescriptionCount]);

  // Lets existing content, such as an Alert's text, describe the dialog
  // without taking on the description's typography and spacing.
  if (asChild) {
    return (
      <DialogPrimitive.Description
        ref={ref}
        data-slot='dialog-description'
        className={className}
        asChild
        {...rest}
      >
        {children}
      </DialogPrimitive.Description>
    );
  }

  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot='dialog-description'
      className={cn(
        'eye:mt-xs eye:mb-2xs eye:text-center eye:sm:text-left',
        'group-data-[has-close-button=true]/dialog-content:eye:mt-2xs',
        className,
      )}
      asChild
      {...rest}
    >
      <Typography variant='body-m' tag='div'>
        {children}
      </Typography>
    </DialogPrimitive.Description>
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogActions,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogDescription,
};
